import AsyncStorage from '@react-native-async-storage/async-storage';
import { onlineManager } from '@tanstack/react-query';
import { isLoggedInAtom, isLoggingOutAtom, selfUserLoadedAtom, userInfoAtom } from 'app/atoms';
import { env } from 'app/lib/env';
import { Event, track } from 'app/lib/analytics';
import { answerSuggestedListing } from 'app/network/suggested-listing';
import { isAxiosError } from 'axios';
import { useAtomValue, useSetAtom } from 'jotai';
import { createContext, useContext, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import { AppState } from 'react-native';
import { OfferHubQueue, type HubQueueSnapshot } from './queue';
import { canUseOfferHub } from 'app/screens/intro/navigator-helper';

const QueueContext = createContext<OfferHubQueue | null>(null);
const EMPTY: HubQueueSnapshot = { ready: false, storageError: false, decisions: [] };
const emptySnapshot = () => EMPTY;
const emptySubscribe = () => () => undefined;

export function OfferHubQueueProvider({ children }: { children: ReactNode }) {
  const loggedIn = useAtomValue(isLoggedInAtom);
  const loggingOut = useAtomValue(isLoggingOutAtom);
  const user = useAtomValue(userInfoAtom);
  const isUserPending = !user;
  const userId =
    loggedIn && !loggingOut && user && canUseOfferHub(user.featureFlags, isUserPending) ? user.id : undefined;
  const setUserLoaded = useSetAtom(selfUserLoadedAtom);
  const callbacks = useRef({ setUserLoaded });
  useEffect(() => {
    callbacks.current = { setUserLoaded };
  }, [setUserLoaded]);
  const [current, setCurrent] = useState<{ userId: string; queue: OfferHubQueue } | null>(null);

  useEffect(() => {
    if (!userId) {
      return;
    }
    const storageKey = `offer-hub-outbox:v2:${encodeURIComponent(env.API_BASE_URL)}:${userId}`;
    let invalidation: ReturnType<typeof setTimeout> | undefined;
    const queue = new OfferHubQueue({
      read: () => AsyncStorage.getItem(storageKey),
      write: (data) => AsyncStorage.setItem(storageKey, data),
      send: async (decision, signal) => {
        await answerSuggestedListing(
          decision.suggestedListingId,
          decision.kind === 'accept' ? 'yes' : 'no',
          decision.source,
          signal,
        );
      },
      classifyError: (error) => {
        if (!onlineManager.isOnline()) return 'retry';
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status === 401) return 'auth';
          if (!status || status >= 500 || status === 429 || status === 408) return 'retry';
          return 'permanent';
        }
        return 'retry';
      },
      onSaved: (decision) => {
        if (decision.kind !== 'accept') return;
        track(Event.QuickOffersPosted, { num_items: 1, suggestedListingIds: [decision.suggestedListingId] });
        clearTimeout(invalidation);
        invalidation = setTimeout(() => {
          callbacks.current.setUserLoaded(false);
        }, 500);
      },
    });
    let disposed = false;
    void Promise.resolve().then(() => {
      if (!disposed) setCurrent({ userId, queue });
    });
    void queue.start();
    const appState = AppState.addEventListener('change', (state) => {
      if (state === 'active') queue.wake();
    });
    const unsubscribe = onlineManager.subscribe((online) => {
      if (online) queue.wake();
    });
    return () => {
      disposed = true;
      queue.stop();
      appState.remove();
      unsubscribe();
      clearTimeout(invalidation);
    };
  }, [userId]);
  return (
    <QueueContext.Provider value={current?.userId === userId ? (current?.queue ?? null) : null}>
      {children}
    </QueueContext.Provider>
  );
}

export function useOfferHubQueue() {
  const queue = useContext(QueueContext);
  const snapshot = useSyncExternalStore(
    queue?.subscribe ?? emptySubscribe,
    queue?.getSnapshot ?? emptySnapshot,
    queue?.getSnapshot ?? emptySnapshot,
  );
  return { queue, ...snapshot };
}
